import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Users, Clock, Utensils, Plus, CheckCircle2, Split, X,
  Coffee, Link as LinkIcon, Layers, Trash2, UserPlus,
  BookMarked, UtensilsCrossed, Map, Loader2, AlertCircle,
  MousePointerClick, ArrowRight, Minus, Search, Tag,
  CreditCard, Banknote, Smartphone, SplitSquareHorizontal,
  Receipt, Printer, Send, Phone, User, MessageSquare,
  ChevronDown, FileText, Edit3, Ban, CheckCheck, Timer,
  ListOrdered, RefreshCw, ChefHat, AlertTriangle, QrCode
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import '../../assets/css/tables.css';
import axios from 'axios';
import { baseurl } from '../../baseurl';
import QRCodePanel from '../CustomerMenu/QRCodePanel';

/* ═══════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════ */
const GST_RATES = [0, 5, 12, 18, 28];

const PAYMENT_METHODS = [
  { id: 'cash',  label: 'Cash',  icon: <Banknote size={12}/>              },
  { id: 'card',  label: 'Card',  icon: <CreditCard size={12}/>            },
  { id: 'upi',   label: 'UPI',   icon: <Smartphone size={12}/>            },
  { id: 'split', label: 'Split', icon: <SplitSquareHorizontal size={12}/> },
];

const ORDER_STATUS_META = {
  cart:         { label: 'Cart',      color: '#8b5cf6', bg: 'rgba(139,92,246,0.10)'  },
  kot:          { label: 'KOT',       color: '#6366f1', bg: 'rgba(99,102,241,0.10)'  },
  placed:       { label: 'Placed',    color: '#ff8c00', bg: 'rgba(255,140,0,0.10)'   },
  preparing:    { label: 'Preparing', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)'  },
  ready:        { label: 'Ready',     color: '#22c55e', bg: 'rgba(34,197,94,0.10)'   },
  served:       { label: 'Served',    color: '#9ca3af', bg: 'rgba(156,163,175,0.10)' },
  cancelled:    { label: 'Cancelled', color: '#ef4444', bg: 'rgba(239,68,68,0.10)'   },
  placed_print: { label: 'Printed',   color: '#0ea5e9', bg: 'rgba(14,165,233,0.10)'  },
};


/* ═══════════════════════════════════════
   ALERT SOUND — Web Audio API (no external file needed)
═══════════════════════════════════════ */
function playAlertSound(type = 'new_order') {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();

    if (type === 'new_order') {
      // Three ascending beeps — distinct "new order" sound
      [0, 0.15, 0.3].forEach((startTime, i) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 520 + i * 120; // 520 → 640 → 760 Hz
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.4, ctx.currentTime + startTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startTime + 0.12);
        osc.start(ctx.currentTime + startTime);
        osc.stop(ctx.currentTime + startTime + 0.12);
      });
    } else if (type === 'item_added') {
      // Single soft chime — "item added to existing order"
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } else if (type === 'table_occupied') {
      // Two quick low beeps — "table just got occupied"
      [0, 0.2].forEach(startTime => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 440;
        osc.type = 'triangle';
        gain.gain.setValueAtTime(0.35, ctx.currentTime + startTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startTime + 0.15);
        osc.start(ctx.currentTime + startTime);
        osc.stop(ctx.currentTime + startTime + 0.15);
      });
    }
  } catch (e) {
    // AudioContext blocked (user hasn't interacted yet) — silent fail
    console.warn('playAlertSound blocked:', e.message);
  }
}


const BLANK_BILLING = {
  customerName: '', customerPhone: '', orderNote: '',
  discountType: 'percent', discountVal: '',
  gstRate: 5, gstType: 'exclusive',
  payMethod: 'cash',
  splitCash: '', splitCard: '', splitUpi: '',
};

/* ═══════════════════════════════════════
   BILLING CALC
═══════════════════════════════════════ */
function calcBill(items, billing) {
  const rate    = (parseFloat(billing.gstRate) || 5) / 100;
  const gstType = billing.gstType || 'exclusive';
  const rawSub  = items.reduce((s, i) => s + parseFloat(i.price) * (i.qty || 1), 0);
  const dv      = parseFloat(billing.discountVal) || 0;
  const disc    = dv <= 0 ? 0 : billing.discountType === 'percent'
    ? Math.min(rawSub, rawSub * dv / 100) : Math.min(rawSub, dv);
  const after   = rawSub - disc;
  let subtotal, taxAmount, grandTotal;
  if (gstType === 'inclusive') {
    taxAmount  = after * rate / (1 + rate);
    subtotal   = after - taxAmount;
    grandTotal = after;
  } else {
    subtotal   = after;
    taxAmount  = after * rate;
    grandTotal = after + taxAmount;
  }
  return { subtotal, discountAmount: disc, taxAmount, grandTotal, gstRate: parseFloat(billing.gstRate) || 5, gstType };
}

/* ═══════════════════════════════════════
   PRINT BILL
═══════════════════════════════════════ */
function printTableBill(order, restaurant = {}) {
  // restaurant can be a string (legacy) or an object {name,tagline,phone,...}
  const prof     = typeof restaurant === 'string' ? { name: restaurant } : (restaurant || {});
  const rName    = prof.name       || 'Restaurant';
  const tagline  = prof.tagline    || '';
  const phone    = prof.phone      || '';
  const address  = [prof.address, prof.city, prof.state, prof.pincode].filter(Boolean).join(', ');
  const gstin    = prof.gst_number || '';
  const footer   = prof.footer_note || 'Thank you for dining with us!';
  const logoUrl  = prof.logo_url   || '';
 
  const items    = order.items || [];
  const dateStr  = new Date().toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
 
  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Bill – ${order.tableLabel || 'Table'}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:'Courier New',monospace;font-size:12px;color:#111;width:80mm;margin:0 auto;padding:10px 8px;}
    .hdr{text-align:center;margin-bottom:6px;}
    .logo{max-width:72px;max-height:52px;display:block;margin:0 auto 5px;}
    .rname{font-size:17px;font-weight:900;letter-spacing:.3px;}
    .tagline{font-size:10px;color:#555;margin-top:2px;}
    .rmeta{font-size:10px;color:#555;margin-top:1px;}
    .gstin{font-size:9px;color:#555;font-weight:700;margin-top:2px;}
    .inv-lbl{text-align:center;font-size:10px;font-weight:900;letter-spacing:3px;text-transform:uppercase;margin:4px 0;}
    .div-s{border-top:1px solid #111;margin:5px 0;}
    .div-d{border-top:1px dashed #999;margin:4px 0;}
    .ometa{display:flex;justify-content:space-between;font-size:10px;margin:2px 0;}
    .cust{font-size:11px;margin:4px 0;line-height:1.5;}
    .cust b{font-weight:800;}
    .col-hdr{display:flex;font-size:10px;font-weight:700;color:#555;}
    .col-hdr .cn{flex:1;} .col-hdr .cq{width:26px;text-align:center;} .col-hdr .cr{width:52px;text-align:right;} .col-hdr .ca{width:58px;text-align:right;}
    .irow{display:flex;align-items:flex-start;font-size:11.5px;margin:3px 0;}
    .irow .cn{flex:1;line-height:1.35;} .irow .cq{width:26px;text-align:center;flex-shrink:0;} .irow .cr{width:52px;text-align:right;color:#555;flex-shrink:0;} .irow .ca{width:58px;text-align:right;font-weight:700;flex-shrink:0;}
    .ht{font-size:9px;color:#999;}
    .trow{display:flex;justify-content:space-between;font-size:12px;font-weight:600;margin:3px 0;}
    .trow.disc{color:#16a34a;}
    .trow.grand{font-size:15px;font-weight:900;margin:5px 0 3px;}
    .trow.pay{font-size:10.5px;color:#555;font-weight:600;}
    .note{font-size:10px;color:#666;font-style:italic;margin-top:3px;}
    .ftr{text-align:center;font-size:10px;color:#555;margin-top:8px;line-height:1.55;}
    .ftr .pw{font-size:9px;color:#bbb;margin-top:2px;}
    @media print{body{width:100%;}}
  </style>
</head>
<body>
 
<div class="hdr">
  ${logoUrl ? `<img src="${logoUrl}" class="logo" alt=""/>` : ''}
  <div class="rname">${rName}</div>
  ${tagline ? `<div class="tagline">${tagline}</div>` : ''}
  ${address ? `<div class="rmeta">${address}</div>` : ''}
  ${phone   ? `<div class="rmeta">&#128222; ${phone}</div>` : ''}
  ${gstin   ? `<div class="gstin">GSTIN: ${gstin}</div>` : ''}
</div>
 
<div class="div-s"></div>
<div class="inv-lbl">Tax Invoice</div>
<div class="div-s"></div>
 
<div class="ometa">
  <span><b>Table:</b> ${order.tableLabel || '—'}</span>
  <span>${dateStr}</span>
</div>
${order.orderNumber ? `<div class="ometa"><span><b>Bill #</b> ${order.orderNumber}</span></div>` : ''}
 
${(order.customerName || order.customerPhone) ? `
<div class="cust">
  ${order.customerName  ? `<div><b>${order.customerName}</b></div>` : ''}
  ${order.customerPhone ? `<div>&#128241; ${order.customerPhone}</div>` : ''}
</div>` : ''}
 
<div class="div-d"></div>
 
<div class="col-hdr">
  <span class="cn">Item</span>
  <span class="cq">Qty</span>
  <span class="cr">Rate</span>
  <span class="ca">Amt</span>
</div>
<div class="div-d"></div>
 
${items.map(i => `
<div class="irow">
  <span class="cn">${i.name}${i.variant === 'half' ? ' <span class="ht">(&#189;)</span>' : ''}</span>
  <span class="cq">${i.qty}</span>
  <span class="cr">&#8377;${parseFloat(i.price).toFixed(2)}</span>
  <span class="ca">&#8377;${(parseFloat(i.price) * parseInt(i.qty)).toFixed(2)}</span>
</div>`).join('')}
 
<div class="div-d"></div>
 
<div class="trow"><span>Subtotal</span><span>&#8377;${parseFloat(order.subtotal || 0).toFixed(2)}</span></div>
 
${parseFloat(order.discountAmount || 0) > 0 ? `
<div class="trow disc"><span>Discount</span><span>–&#8377;${parseFloat(order.discountAmount).toFixed(2)}</span></div>` : ''}
 
<div class="trow">
  <span>GST (${order.gstRate || 5}%${order.gstType === 'inclusive' ? ' incl.' : ''})</span>
  <span>&#8377;${parseFloat(order.taxAmount || 0).toFixed(2)}</span>
</div>
 
<div class="div-s"></div>
 
<div class="trow grand">
  <span>TOTAL</span>
  <span>&#8377;${parseFloat(order.grandTotal || 0).toFixed(2)}</span>
</div>
 
<div class="trow pay">
  <span>Payment</span>
  <span>${(order.payMethod || 'cash').toUpperCase()}${order.payMethod === 'split'
    ? ` (Cash &#8377;${parseFloat(order.splitCash||0).toFixed(0)} · Card &#8377;${parseFloat(order.splitCard||0).toFixed(0)} · UPI &#8377;${parseFloat(order.splitUpi||0).toFixed(0)})`
    : ''
  }</span>
</div>
 
${order.orderNote ? `<div class="note">&#128221; ${order.orderNote}</div>` : ''}
 
<div class="div-d"></div>
 
<div class="ftr">
  <div>${footer}</div>
  <div class="pw">Powered by Restrowave POS</div>
</div>
 
<script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();}<\/script>
</body>
</html>`;
 
  const w = window.open('', '_blank', 'width=360,height=660');
  if (w) { w.document.write(html); w.document.close(); }
}
 






function sendBillWhatsApp(order, restaurantName = 'Restrowave Restaurant') {
  const items = order.items || [];
  const lines = items.map(i => `  ${i.name}${i.variant === 'half' ? '(½)' : ''} x${i.qty} = ₹${(parseFloat(i.price) * parseInt(i.qty)).toFixed(2)}`).join('\n');
  const msg = `🧾 *${restaurantName}*\nTable: ${order.tableLabel || '—'}\n━━━━━━━━━━━━━━\n${lines}\n━━━━━━━━━━━━━━\nSubtotal: ₹${parseFloat(order.subtotal || 0).toFixed(2)}\nGST: ₹${parseFloat(order.taxAmount || 0).toFixed(2)}\n*Total: ₹${parseFloat(order.grandTotal || 0).toFixed(2)}*\nPayment: ${(order.payMethod || 'cash').toUpperCase()}\n\nThank you! 🙏`;
  const phone = (order.customerPhone || '').replace(/\D/g, '');
  window.open(`https://wa.me/${phone ? `91${phone}` : ''}?text=${encodeURIComponent(msg)}`, '_blank');
}

/* ═══════════════════════════════════════
   API HELPER
═══════════════════════════════════════ */
const api = () => {
  const token = localStorage.getItem('restaurant_token');
  return axios.create({ baseURL: baseurl, headers: { Authorization: `Bearer ${token}` } });
};

function useToast() {
  const [toast, setToast] = useState({ show: false, msg: '', type: 'success' });
  const fire = useCallback((msg, type = 'success') => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 2600);
  }, []);
  return { toast, fire };
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════ */
function Tables() {
  const [zones,            setZones]            = useState([]);
  const [tables,           setTables]           = useState([]);
  const [menuItems,        setMenuItems]        = useState([]);
  const [categories,       setCategories]       = useState([]);
  const [menuLoading,      setMenuLoading]      = useState(false);
  const [menuSearch,       setMenuSearch]       = useState('');
  const [menuCat,          setMenuCat]          = useState('all');
  const [activeZoneId,     setActiveZoneId]     = useState('all');
  const [selectedId,       setSelectedId]       = useState(null);
  const [mergeMode,        setMergeMode]        = useState(false);
  const [mergeSelection,   setMergeSelection]   = useState([]);
  const [showOrder,        setShowOrder]        = useState(false);
  const [guestCount,       setGuestCount]       = useState(2);
  const [loading,          setLoading]          = useState(true);
  const [saving,           setSaving]           = useState(false);
  const [sidebarTab,       setSidebarTab]       = useState('order');
  const [showDiscount,     setShowDiscount]     = useState(false);
  const [openOrders,       setOpenOrders]       = useState([]);
  const [ordersLoading,    setOrdersLoading]    = useState(false);
  const [placing,          setPlacing]          = useState(false);
  const [showBillModal,    setShowBillModal]    = useState(null);
  const [cancelModalOrder, setCancelModalOrder] = useState(null);
  const [cancelReason,     setCancelReason]     = useState('');
  const [completingId,     setCompletingId]     = useState(null);
  const [restaurantName,   setRestaurantName]   = useState(localStorage.getItem('restaurant_name') || 'Restrowave Restaurant');

  // ── Idle refresh + new order alert state ──
  const [newOrderAlert,    setNewOrderAlert]    = useState(null);  // { type, message, orderNumber }
  const idleTimerRef       = useRef(null);
  const lastOrderCountRef  = useRef(null); // track order count to detect new arrivals
  const lastTableStatusRef = useRef({});   // track table statuses to detect QR occupations
  const lastInteractionRef = useRef(Date.now());
  const audioUnlockedRef   = useRef(false);


  const [restaurantProfile, setRestaurantProfile] = useState({
  name:        localStorage.getItem('restaurant_name') || 'Restaurant',
  tagline:     '',
  phone:       '',
  address:     '',
  city:        '',
  state:       '',
  pincode:     '',
  gst_number:  '',
  logo_url:    '',
  footer_note: '',
});

  // QR panel state
  const [showQR,  setShowQR]  = useState(false);
  const [qrTable, setQrTable] = useState(null);

  // DB-driven per-table state
  const [tableItemsMap,   setTableItemsMap]   = useState({});
  const [tableGuestsMap,  setTableGuestsMap]  = useState({});
  const [tableBillingMap, setTableBillingMap] = useState({});

  // Track which tables are currently syncing their cart to DB
  const cartSyncing    = useRef({});
  const pollIntervalRef = useRef(null);

  const { toast, fire } = useToast();

  /* ─────────────────────────────────────────────────────────────────
     DERIVED STATE — must be defined before any useCallback that uses them
  ───────────────────────────────────────────────────────────────── */
  const selectedTable = useMemo(() =>
    tables.find(t => t.id === selectedId) ?? null,
    [selectedId, tables]
  );

  const billingKey = useMemo(() =>
    selectedTable
      ? (selectedTable.groupId ? `group_${selectedTable.groupId}` : `table_${selectedTable.id}`)
      : null,
    [selectedTable]
  );

  const billing = useMemo(() =>
    billingKey ? (tableBillingMap[billingKey] || { ...BLANK_BILLING }) : { ...BLANK_BILLING },
    [billingKey, tableBillingMap]
  );

  /* ─────────────────────────────────────────────────────────────────
     CALLBACKS — defined before any useEffect that references them
  ───────────────────────────────────────────────────────────────── */

  /* fetchTableCart — always pull from DB, never trust local state */
const fetchTableCart = useCallback(async (tableId) => {
  try {
    const r = await api().get(`/orders/active-for-table/${tableId}`);
    const cartOrder   = r.data.order;        // status='cart' staging order
    const activeOrder = r.data.active_order; // status=placed/kot/preparing/ready

    const cartItems = cartOrder
      ? (cartOrder.items || []).map(si => ({
          cartId:     `${si.menu_item_id || si.name}_${si.variant || 'full'}`,
          menuItemId: si.menu_item_id || null,
          name:       si.name,
          variant:    si.variant || 'full',
          price:      parseFloat(si.price),
          qty:        parseInt(si.qty),
          note:       si.note || '',
          fromQR:     cartOrder.order_source === 'qr',
        }))
      : [];

    setTableItemsMap(prev => ({ ...prev, [tableId]: cartItems }));
    setTables(prev => prev.map(t => t.id === tableId ? { ...t, items: cartItems } : t));

    // ── Pre-fill billing from cart order first, then fall back to active order ──
    // This ensures name/phone is always shown when table is occupied
    const infoSource = cartOrder || activeOrder; // prefer cart, fall back to active
    if (infoSource?.customer_name || infoSource?.customer_phone) {
      setTableBillingMap(prev => {
        const bKey = selectedTable?.groupId
          ? `group_${selectedTable.groupId}`
          : `table_${tableId}`;
        const cur = prev[bKey] || { ...BLANK_BILLING };

        // Only auto-fill if the user hasn't already typed something
        if (cur.customerName || cur.customerPhone) return prev;

        return {
          ...prev,
          [bKey]: {
            ...cur,
            customerName:  infoSource.customer_name  || '',
            customerPhone: infoSource.customer_phone || '',
            orderNote:     infoSource.order_note     || '',
            // Also carry over payment method and GST from active order if present
            ...(activeOrder && {
              gstRate:  activeOrder.gst_rate  || cur.gstRate,
              gstType:  activeOrder.gst_type  || cur.gstType,
              payMethod: activeOrder.payment_method || cur.payMethod,
            }),
          },
        };
      });
    }
  } catch (_) { /* silent */ }
}, [selectedTable]);

  /* pushCartToDb — writes full item state to DB immediately */
  const pushCartToDb = useCallback(async (tableId, items, billingData) => {
    if (!tableId) return;
    if (cartSyncing.current[tableId]) {
      cartSyncing.current[`${tableId}_pending`] = { items, billing: billingData };
      return;
    }
    cartSyncing.current[tableId] = true;
    try {
      if (items.length === 0) {
        await api().delete(`/orders/cart/${tableId}`);
      } else {
        await api().put(`/orders/cart/${tableId}`, {
          customer_name:  billingData.customerName  || null,
          customer_phone: billingData.customerPhone || null,
          order_note:     billingData.orderNote     || null,
          gst_rate:       billingData.gstRate || 5,
          gst_type:       billingData.gstType || 'exclusive',
          items: items.map(i => ({
            menu_item_id: i.menuItemId || null,
            name:    i.name,
            variant: i.variant,
            price:   i.price,
            qty:     i.qty,
            note:    i.note || null,
          })),
        });
      }
    } catch (e) {
      console.error('pushCartToDb failed:', e);
    } finally {
      cartSyncing.current[tableId] = false;
      const pending = cartSyncing.current[`${tableId}_pending`];
      if (pending) {
        delete cartSyncing.current[`${tableId}_pending`];
        pushCartToDb(tableId, pending.items, pending.billing);
      }
    }
  }, []);

  /* clearCartInDb */
  const clearCartInDb = useCallback(async (tableId) => {
    if (!tableId) return;
    try { await api().delete(`/orders/cart/${tableId}`); } catch (_) {}
  }, []);

  /* ─────────────────────────────────────────────────────────────────
     EFFECTS — all after callbacks are defined
  ───────────────────────────────────────────────────────────────── */
  useEffect(() => { loadData(); loadMenu(); }, []); // eslint-disable-line

  useEffect(() => {
    if (sidebarTab === 'open_orders') loadOpenOrders();
  }, [sidebarTab]); // eslint-disable-line

  // Fetch cart from DB when a table is selected and occupied
  useEffect(() => {
    if (!selectedId) return;
    const t = tables.find(x => x.id === selectedId);
    if (t && t.status !== 'available') {
      fetchTableCart(selectedId);
    }
  }, [selectedId]); // eslint-disable-line

  // Auto-poll cart every 10s when a table is selected and occupied
  // Keeps POS in sync when customer adds items via QR
  useEffect(() => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (!selectedId) return;
    const t = tables.find(x => x.id === selectedId);
    if (!t || t.status === 'available') return;

    pollIntervalRef.current = setInterval(async () => {
      if (!cartSyncing.current[selectedId]) {
        await fetchTableCart(selectedId);
      }
    }, 10000);

    return () => clearInterval(pollIntervalRef.current);
  }, [selectedId, tables, fetchTableCart]);





// ── Unlock AudioContext on first user interaction ──
  useEffect(() => {
    const unlock = () => {
      if (!audioUnlockedRef.current) {
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          ctx.resume().then(() => { audioUnlockedRef.current = true; });
        } catch (_) {}
      }
    };
    window.addEventListener('click',   unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('click',   unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  // ── Track last interaction time (reset idle timer on any mouse/key activity) ──
  useEffect(() => {
    const resetIdle = () => { lastInteractionRef.current = Date.now(); };
    window.addEventListener('mousemove',  resetIdle);
    window.addEventListener('mousedown',  resetIdle);
    window.addEventListener('keydown',    resetIdle);
    window.addEventListener('touchstart', resetIdle);
    return () => {
      window.removeEventListener('mousemove',  resetIdle);
      window.removeEventListener('mousedown',  resetIdle);
      window.removeEventListener('keydown',    resetIdle);
      window.removeEventListener('touchstart', resetIdle);
    };
  }, []);

  // ── Idle refresh — if no interaction for 30s, refresh tables + open orders ──
  useEffect(() => {
    const IDLE_MS = 30_000;

    const tick = async () => {
      const idleMs = Date.now() - lastInteractionRef.current;
      if (idleMs >= IDLE_MS) {
        // Refresh tables silently
        try {
          const tablesRes = await api().get('/tables');
          const freshTables = (tablesRes.data.tables || []).map(t => ({
            ...t,
            items:   [],
            guests:  t.guests  || 0,
            timer:   t.timer   || null,
            groupId: t.group_id || t.groupId || null,
          }));

          // ── Detect newly occupied tables (QR scan happened while idle) ──
          setTables(prev => {
            const prevMap = {};
            prev.forEach(t => { prevMap[t.id] = t.status; });

            const newlyOccupied = freshTables.filter(t =>
              prevMap[t.id] === 'available' && t.status === 'occupied'
            );

            if (newlyOccupied.length > 0) {
              const labels = newlyOccupied.map(t => t.label).join(', ');
              playAlertSound('table_occupied');
              setNewOrderAlert({
                type:    'table_occupied',
                message: `Table ${labels} just got occupied via QR scan`,
                icon:    '📱',
              });
            }

            // Preserve existing items/billing state — only update status fields
            return freshTables.map(ft => {
              const existing = prev.find(p => p.id === ft.id);
              return existing
                ? { ...existing, status: ft.status, timer: ft.timer, guests: ft.guests }
                : ft;
            });
          });

        } catch (_) { /* silent */ }

        // Refresh open orders
        try {
          const results = await Promise.all(
            ['placed', 'kot', 'preparing', 'ready'].map(s =>
              api().get(`/orders?status=${s}&limit=50&days=3`).catch(() => ({ data: { orders: [] } }))
            )
          );
          const fresh = results
            .flatMap(r => r.data.orders || [])
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

          setOpenOrders(prev => {
            // ── Detect brand new orders ──
            const prevIds = new Set(prev.map(o => o.id));
            const newOrders = fresh.filter(o => !prevIds.has(o.id));

            if (newOrders.length > 0) {
              const nums = newOrders.map(o => `#${o.order_number}`).join(', ');
              playAlertSound('new_order');
              setNewOrderAlert({
                type:    'new_order',
                message: `New order${newOrders.length > 1 ? 's' : ''} arrived: ${nums}`,
                icon:    '🔔',
              });
            }

            // ── Detect items added to existing orders (grand_total changed) ──
            const updatedOrders = fresh.filter(o => {
              const existing = prev.find(p => p.id === o.id);
              return existing && parseFloat(o.grand_total) > parseFloat(existing.grand_total);
            });

            if (updatedOrders.length > 0 && newOrders.length === 0) {
              const nums = updatedOrders.map(o => `#${o.order_number}`).join(', ');
              playAlertSound('item_added');
              setNewOrderAlert({
                type:    'item_added',
                message: `Items added to order${updatedOrders.length > 1 ? 's' : ''}: ${nums}`,
                icon:    '🍽️',
              });
            }

            return fresh;
          });
        } catch (_) { /* silent */ }
      }
    };

    idleTimerRef.current = setInterval(tick, 5000); // check every 5s
    return () => clearInterval(idleTimerRef.current);
  }, []); // eslint-disable-line

  // ── Auto-dismiss alert after 6 seconds ──
  useEffect(() => {
    if (!newOrderAlert) return;
    const t = setTimeout(() => setNewOrderAlert(null), 10000);
    return () => clearTimeout(t);
  }, [newOrderAlert]);



  /* ─────────────────────────────────────────────────────────────────
     LOAD DATA
  ───────────────────────────────────────────────────────────────── */
const loadData = async () => {
  setLoading(true);
  try {
    const [zonesRes, tablesRes, profileRes] = await Promise.all([
      api().get('/zones'),
      api().get('/tables'),
      api().get('/settings/profile').catch(() => ({ data: { restaurant: {} } })),
    ]);
    setZones(zonesRes.data.zones || []);
    setTables((tablesRes.data.tables || []).map(t => ({
      ...t,
      items:   [],
      guests:  t.guests  || 0,
      timer:   t.timer   || null,
      groupId: t.group_id || t.groupId || null,
    })));
 
    const prof = profileRes.data.restaurant || {};
    if (prof.name) {
      setRestaurantName(prof.name);
      localStorage.setItem('restaurant_name', prof.name);
    }
    setRestaurantProfile(prev => ({ ...prev, ...prof }));
 
  } catch { fire('Failed to load tables', 'error'); }
  finally { setLoading(false); }
};
 

  const loadMenu = async () => {
    setMenuLoading(true);
    try {
      const [catR, itemR] = await Promise.all([
        api().get('/categories').catch(() => ({ data: { categories: [] } })),
        api().get('/items').catch(()       => ({ data: { items: [] } })),
      ]);
      setCategories(catR.data.categories || []);
      setMenuItems(
        (itemR.data.items || itemR.data.products || [])
          .map(p => ({
            ...p,
            tags:           Array.isArray(p.tags) ? p.tags : (p.tags ? JSON.parse(p.tags) : []),
            is_available:   Boolean(p.is_available !== undefined ? p.is_available : true),
            has_half_plate: Boolean(p.has_half_plate),
            price_full:     parseFloat(p.price_full ?? p.price ?? 0),
            price_half:     p.price_half ? parseFloat(p.price_half) : null,
          }))
          .filter(p => p.is_available)
      );
    } catch { fire('Failed to load menu', 'error'); }
    finally { setMenuLoading(false); }
  };

  const loadOpenOrders = async () => {
    setOrdersLoading(true);
    try {
      const results = await Promise.all(
        ['placed', 'kot', 'preparing', 'ready'].map(s =>
          api().get(`/orders?status=${s}&limit=50&days=3`).catch(() => ({ data: { orders: [] } }))
        )
      );
      setOpenOrders(
        results.flatMap(r => r.data.orders || [])
               .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      );
    } finally { setOrdersLoading(false); }
  };

  /* ── Filtered menu ── */
  const filteredMenu = useMemo(() =>
    menuItems.filter(p =>
      (menuCat === 'all' || p.category === menuCat || String(p.category_id) === menuCat) &&
      p.name.toLowerCase().includes(menuSearch.toLowerCase())
    ), [menuItems, menuCat, menuSearch]);

  /* ── Patch helpers ── */
  const patchTableGuests = useCallback((tableId, val) => {
    setTableGuestsMap(prev => ({ ...prev, [tableId]: val }));
    setTables(prev => prev.map(t => t.id === tableId ? { ...t, guests: val } : t));
  }, []);

  const patchBilling = useCallback((bKey, patch) => {
    setTableBillingMap(prev => {
      const cur = prev[bKey] || { ...BLANK_BILLING };
      return { ...prev, [bKey]: { ...cur, ...patch } };
    });
  }, []);

  const patchActiveBilling = useCallback((patch) => {
    if (!billingKey) return;
    patchBilling(billingKey, patch);
    if (selectedId) {
      const items = tableItemsMap[selectedId] || [];
      if (items.length) pushCartToDb(selectedId, items, { ...billing, ...patch });
    }
  }, [billingKey, patchBilling, selectedId, tableItemsMap, billing, pushCartToDb]);

  /* ── Group context ── */
  const groupContext = useMemo(() => {
    if (!selectedTable) return null;
    const members       = selectedTable.groupId
      ? tables.filter(t => t.groupId === selectedTable.groupId)
      : [selectedTable];
    const isGroup       = members.length > 1;
    const totalCapacity = members.reduce((s, t) => s + (t.capacity || 0), 0);
    const totalGuests   = members.reduce((s, t) => s + (t.guests   || 0), 0);
    const allItems      = members.flatMap(t =>
      (tableItemsMap[t.id] || t.items || []).map(item => ({ ...item, tableLabel: t.label, tableId: t.id }))
    );
    const bill     = calcBill(allItems, billing);
    const statuses = members.map(t => t.status);
    const status   = statuses.includes('attention') ? 'attention'
                   : statuses.includes('occupied')  ? 'occupied'
                   : statuses.every(s => s === 'reserved') ? 'reserved'
                   : 'available';
    return { members, isGroup, totalCapacity, totalGuests, allItems, bill, status };
  }, [selectedTable, tables, tableItemsMap, billing]);

  const splitTotal = useMemo(() =>
    (parseFloat(billing.splitCash) || 0) +
    (parseFloat(billing.splitCard) || 0) +
    (parseFloat(billing.splitUpi)  || 0),
    [billing]
  );

  const filteredTables = useMemo(() => {
    const base = activeZoneId === 'all'
      ? tables
      : tables.filter(t => String(t.zone_id) === String(activeZoneId));
    const seenGroups = new Set();
    const merged     = [];
    base.filter(t => t.groupId).forEach(t => {
      if (!seenGroups.has(t.groupId)) {
        seenGroups.add(t.groupId);
        merged.push({ ...t, _groupMembers: base.filter(x => x.groupId === t.groupId) });
      }
    });
    return {
      merged,
      occupied:  base.filter(t => t.status !== 'available' && !t.groupId),
      available: base.filter(t => t.status === 'available'  && !t.groupId),
    };
  }, [tables, activeZoneId]);

  const stats = useMemo(() => ({
    available: tables.filter(t => t.status === 'available').length,
    occupied:  tables.filter(t => t.status === 'occupied').length,
    reserved:  tables.filter(t => t.status === 'reserved').length,
    attention: tables.filter(t => t.status === 'attention').length,
  }), [tables]);

  const qrItemCount = useMemo(() => {
    if (!selectedId) return 0;
    return (tableItemsMap[selectedId] || []).filter(i => i.fromQR).length;
  }, [selectedId, tableItemsMap]);

  /* ── Merge mode ── */
  const enterMergeMode  = () => { setMergeMode(true);  setMergeSelection([]); setSelectedId(null); };
  const cancelMergeMode = () => { setMergeMode(false); setMergeSelection([]); };

  const toggleMergeTable = t =>
    setMergeSelection(prev =>
      prev.includes(t.id) ? prev.filter(id => id !== t.id) : [...prev, t.id]
    );

  const handleConfirmMerge = async () => {
    if (mergeSelection.length < 2) { fire('Select at least 2 tables'); return; }
    const newGroupId = `G${Date.now().toString(36).slice(-4).toUpperCase()}`;
    setSaving(true);
    try {
      await Promise.all(mergeSelection.map(id => api().put(`/tables/${id}`, { group_id: newGroupId })));
      setTables(prev => prev.map(t => mergeSelection.includes(t.id) ? { ...t, groupId: newGroupId } : t));
      setMergeMode(false); setMergeSelection([]);
      fire(`${mergeSelection.length} tables merged → Group ${newGroupId}`);
    } catch { fire('Failed to merge tables', 'error'); }
    finally { setSaving(false); }
  };

  const handleTableClick = (table) => {
    if (mergeMode) { toggleMergeTable(table); return; }
    setSelectedId(table.id);
    setGuestCount(
      table.groupId
        ? tables.filter(t => t.groupId === table.groupId).reduce((s, t) => s + (t.capacity || 0), 0)
        : table.capacity || 2
    );
    setMenuSearch(''); setMenuCat('all'); setShowDiscount(false);
    setSidebarTab('order');
  };

  /* ── Session management ── */
  const handleOpenTable = async () => {
    if (!groupContext) return;
    setSaving(true);
    try {
      const { members, totalCapacity } = groupContext;
      const shares = []; let remaining = guestCount;
      members.forEach((t, idx) => {
        const share = idx === members.length - 1
          ? remaining
          : Math.round((t.capacity / totalCapacity) * guestCount);
        shares.push(share); remaining -= share;
      });
      await Promise.all(members.map(async (t, idx) => {
        await api().put(`/tables/${t.id}/status`, { status: 'occupied' });
        await api().put(`/tables/${t.id}`, { guests: shares[idx] });
      }));
      setTables(prev => prev.map(t => {
        const idx = members.findIndex(m => m.id === t.id);
        if (idx === -1) return t;
        return { ...t, status: 'occupied', guests: shares[idx], timer: '0m' };
      }));
      members.forEach((t, idx) => patchTableGuests(t.id, shares[idx]));
      fire(`Session started · ${guestCount} guests${groupContext.isGroup ? ` across ${members.length} tables` : ''}`);
    } catch { fire('Failed to open table', 'error'); }
    finally { setSaving(false); }
  };

  const handleReserveTable = async () => {
    if (!groupContext) return; setSaving(true);
    try {
      await Promise.all(groupContext.members.map(t => api().put(`/tables/${t.id}/status`, { status: 'reserved' })));
      setTables(prev => prev.map(t =>
        groupContext.members.find(m => m.id === t.id) ? { ...t, status: 'reserved' } : t
      ));
      fire(`${groupContext.isGroup ? 'Group' : 'Table'} reserved`);
    } catch { fire('Failed to reserve', 'error'); }
    finally { setSaving(false); }
  };

  const handleCancelReservation = async () => {
    if (!groupContext) return;
    try {
      await Promise.all(groupContext.members.map(t => api().put(`/tables/${t.id}/status`, { status: 'available' })));
      setTables(prev => prev.map(t =>
        groupContext.members.find(m => m.id === t.id) ? { ...t, status: 'available' } : t
      ));
      setSelectedId(null);
      fire('Reservation cancelled');
    } catch { fire('Failed to cancel reservation', 'error'); }
  };

  /* ── Cart operations ── */

  /* handleAddItem — optimistic UI + immediate DB write */
  const handleAddItem = useCallback(async (product, variant = 'full') => {
    const targetId = selectedId;
    if (!targetId) return;
    const price  = variant === 'half' ? (product.price_half || product.price_full) : product.price_full;
    const cartId = `${product.id}_${variant}`;

    // ── Calculate updatedItems BEFORE setState to avoid closure timing issues ──
    const currentItems = tableItemsMap[targetId] || [];
    const existing     = currentItems.find(i => i.cartId === cartId);
    const updatedItems = existing
      ? currentItems.map(i => i.cartId === cartId ? { ...i, qty: i.qty + 1 } : i)
      : [...currentItems, { cartId, menuItemId: product.id, name: product.name, variant, price, qty: 1, note: '' }];

    // Optimistic UI update
    setTableItemsMap(prev => ({ ...prev, [targetId]: updatedItems }));
    setTables(prev => prev.map(t => t.id === targetId ? { ...t, items: updatedItems } : t));

    fire(`${product.name}${variant === 'half' ? ' (½)' : ''} added`);

    // Write to DB immediately — DB is truth
    await pushCartToDb(targetId, updatedItems, billing);
    // Re-fetch to get server-confirmed state (catches QR items added simultaneously)
    await fetchTableCart(targetId);
  }, [selectedId, tableItemsMap, billing, pushCartToDb, fetchTableCart]);

  const handleUpdateItemQty = useCallback(async (tableId, cartId, delta) => {
    // Calculate before setState to avoid closure timing issues
    const currentItems = tableItemsMap[tableId] || [];
    const updatedItems = currentItems
      .map(i => i.cartId === cartId ? { ...i, qty: Math.max(0, i.qty + delta) } : i)
      .filter(i => i.qty > 0);

    setTableItemsMap(prev => ({ ...prev, [tableId]: updatedItems }));
    setTables(prev => prev.map(t => t.id === tableId ? { ...t, items: updatedItems } : t));

    const bl = tableBillingMap[`table_${tableId}`] || billing;
    if (updatedItems.length === 0) {
      await clearCartInDb(tableId);
    } else {
      await pushCartToDb(tableId, updatedItems, bl);
    }
  }, [tableItemsMap, billing, tableBillingMap, pushCartToDb, clearCartInDb]);

  const handleRemoveItem = useCallback(async (tableId, cartId) => {
    // Calculate before setState to avoid closure timing issues
    const currentItems = tableItemsMap[tableId] || [];
    const updatedItems = currentItems.filter(i => i.cartId !== cartId);

    setTableItemsMap(prev => ({ ...prev, [tableId]: updatedItems }));
    setTables(prev => prev.map(t => t.id === tableId ? { ...t, items: updatedItems } : t));

    if (updatedItems.length === 0) {
      await clearCartInDb(tableId);
    } else {
      await pushCartToDb(tableId, updatedItems, tableBillingMap[`table_${tableId}`] || billing);
    }
  }, [tableItemsMap, billing, tableBillingMap, pushCartToDb, clearCartInDb]);




  /* handlePlaceOrder — CONFIRM the existing DB cart, don't re-send items */
  const handlePlaceOrder = useCallback(async (status = 'placed') => {
  if (!groupContext || groupContext.allItems.length === 0) { fire('Add items first', 'error'); return; }
  if (!billing.customerName?.trim())  { fire('Customer name is required', 'error'); return; }
  if (!billing.customerPhone?.trim()) { fire('Customer phone is required', 'error'); return; }
  if (billing.payMethod === 'split' && Math.abs(splitTotal - groupContext.bill.grandTotal) > 1) {
    fire(`Split must equal ₹${groupContext.bill.grandTotal.toFixed(2)}`, 'error'); return;
  }

  setPlacing(true);
  const b = groupContext.bill;

  const confirmPayload = {
    status,
    payment_method:  billing.payMethod,
    split_cash:      billing.payMethod === 'split' ? parseFloat(billing.splitCash)  || 0 : null,
    split_card:      billing.payMethod === 'split' ? parseFloat(billing.splitCard)  || 0 : null,
    split_upi:       billing.payMethod === 'split' ? parseFloat(billing.splitUpi)   || 0 : null,
    discount_type:   billing.discountVal ? billing.discountType : null,
    discount_value:  parseFloat(billing.discountVal) || 0,
    discount_amount: b.discountAmount,
    gst_rate:        b.gstRate,
    gst_type:        b.gstType,
    customer_name:   billing.customerName  || null,
    customer_phone:  billing.customerPhone || null,
    order_note:      billing.orderNote     || null,
  };

  try {
    const targets = groupContext.isGroup ? groupContext.members : [selectedTable];
    const tablesWithItems = targets.filter(t => (tableItemsMap[t.id] || []).length > 0);

    if (tablesWithItems.length === 0) {
      fire('No items in cart — add items first', 'error');
      return;
    }

    const results = [];
    for (const t of tablesWithItems) {
      const r = await api().put(`/orders/cart/${t.id}/confirm`, confirmPayload);
      results.push(r);
    }

    const firstResult = results[0]?.data;
    const firstOrder  = firstResult?.order;

    if (!firstOrder) {
      fire('Order confirmed but no data returned', 'error');
      return;
    }

    if (status === 'placed_print') {
      printTableBill({
  ...b,
  items:         groupContext.allItems,
  tableLabel:    groupContext.isGroup
                   ? groupContext.members.map(m => m.label).join('+')
                   : selectedTable?.label,
  orderNumber:   firstOrder?.order_number,
  customerName:  billing.customerName,
  customerPhone: billing.customerPhone,
  orderNote:     billing.orderNote,
  payMethod:     billing.payMethod,
  splitCash:     billing.splitCash,
  splitCard:     billing.splitCard,
  splitUpi:      billing.splitUpi,
}, restaurantProfile);
    }

    // ── Show different message for append vs new order ──
    if (firstResult.appended) {
      fire(`Items added to Order #${firstOrder.order_number} 🍽️`);
    } else {
      fire(`Order #${firstOrder.order_number} placed! 🎉`);
    }

    handleClearAfterOrder();
    loadOpenOrders();

  } catch (err) {
    console.error('handlePlaceOrder error:', err);
    console.error('Response:', err.response?.data);
    fire(err.response?.data?.message || `Failed (${err.response?.status || err.message})`, 'error');
  } finally {
    setPlacing(false);
  }
}, [groupContext, billing, splitTotal, selectedTable, tableItemsMap, restaurantName]); // eslint-disable-line







  const handleClearAfterOrder = () => {
    if (!groupContext) return;
    setTableItemsMap(prev => {
      const u = { ...prev };
      groupContext.members.forEach(t => delete u[t.id]);
      return u;
    });
    setTableBillingMap(prev => {
      const u = { ...prev };
      if (billingKey) delete u[billingKey];
      return u;
    });
    setTables(prev => prev.map(t =>
      groupContext.members.find(m => m.id === t.id) ? { ...t, items: [] } : t
    ));
  };

  /* ── Settle / Unlink / Flag ── */
  const handleSettleBill = async () => {
    if (!groupContext) return; setSaving(true);
    try {
      await Promise.all(groupContext.members.map(t => api().put(`/tables/${t.id}/status`, { status: 'available' })));
      await Promise.all(groupContext.members.map(t => clearCartInDb(t.id)));
      setTableItemsMap(prev => {
        const u = { ...prev }; groupContext.members.forEach(t => delete u[t.id]); return u;
      });
      setTableGuestsMap(prev => {
        const u = { ...prev }; groupContext.members.forEach(t => delete u[t.id]); return u;
      });
      setTableBillingMap(prev => {
        const u = { ...prev }; if (billingKey) delete u[billingKey]; return u;
      });
      setTables(prev => prev.map(t =>
        groupContext.members.find(m => m.id === t.id)
          ? { ...t, status: 'available', timer: null, guests: 0, items: [], groupId: null }
          : t
      ));
      setSelectedId(null);
      fire('Bill settled — tables now available');
    } catch { fire('Failed to settle bill', 'error'); }
    finally { setSaving(false); }
  };

  const handleUnlink = async () => {
    if (!selectedTable?.groupId) return;
    const members = tables.filter(t => t.groupId === selectedTable.groupId);
    setSaving(true);
    try {
      await Promise.all(members.map(t => api().put(`/tables/${t.id}`, { group_id: null })));
      setTables(prev => prev.map(t =>
        t.groupId === selectedTable.groupId ? { ...t, groupId: null } : t
      ));
      fire('Group split into individual tables');
    } catch { fire('Failed to split group', 'error'); }
    finally { setSaving(false); }
  };

  const handleMarkAttention = async () => {
    if (!groupContext) return;
    const next = groupContext.status === 'attention' ? 'occupied' : 'attention';
    try {
      await Promise.all(groupContext.members.map(t => api().put(`/tables/${t.id}/status`, { status: next })));
      setTables(prev => prev.map(t =>
        groupContext.members.find(m => m.id === t.id) ? { ...t, status: next } : t
      ));
      fire(next === 'attention' ? 'Flagged for attention' : 'Flag removed');
    } catch { fire('Failed to update', 'error'); }
  };

  const handleCompleteOrder = async order => {
    setCompletingId(order.id);
    try {
      await api().put(`/orders/${order.id}/status`, { status: 'served', payment_status: 'paid' });
      fire(`Order #${order.order_number} completed ✓`);
      setOpenOrders(prev => prev.filter(o => o.id !== order.id));
      setTables(prev => prev.map(t =>
        String(t.id) === String(order.table_id) ? { ...t, status: 'available' } : t
      ));
    } catch { fire('Failed to complete order', 'error'); }
    finally { setCompletingId(null); }
  };

  const handleCancelOrder = async () => {
    if (!cancelModalOrder) return;
    if (!cancelReason.trim()) { fire('Please enter a cancellation reason', 'error'); return; }
    try {
      await api().put(`/orders/${cancelModalOrder.id}/status`, {
        status: 'cancelled', cancelled_reason: cancelReason.trim(),
      });
      fire(`Order #${cancelModalOrder.order_number} cancelled`);
      setOpenOrders(prev => prev.filter(o => o.id !== cancelModalOrder.id));
      setTables(prev => prev.map(t =>
        String(t.id) === String(cancelModalOrder.table_id) ? { ...t, status: 'available' } : t
      ));
      setCancelModalOrder(null); setCancelReason('');
    } catch { fire('Failed to cancel order', 'error'); }
  };

  /* ═══════════════════════════════════════
     RENDER
  ═══════════════════════════════════════ */
  if (loading) return (
    <div className="tw-root"><Navbar/>
      <div className="tw-loading">
        <Loader2 size={36} className="tw-spin"/>
        <p style={{ fontWeight:600, color:'var(--muted)' }}>Loading tables…</p>
      </div>
    </div>
  );

  const isOccupied = groupContext && (groupContext.status === 'occupied' || groupContext.status === 'attention');

  return (
    <div className="tw-root">
      <Navbar />
      <div className="tw-main">

        {/* Zone Sidebar */}
        <aside className="tw-zone-sidebar">
          <button
            className={`tw-zone-btn ${activeZoneId === 'all' ? 'active' : ''}`}
            onClick={() => setActiveZoneId('all')}
          >
            <span className="tw-zone-icon"><Map size={15}/></span>All
          </button>
          {zones.map(z => (
            <button
              key={z.id}
              className={`tw-zone-btn ${String(activeZoneId) === String(z.id) ? 'active' : ''}`}
              onClick={() => setActiveZoneId(z.id)}
            >
              <span className="tw-zone-icon"><UtensilsCrossed size={13}/></span>{z.name}
            </button>
          ))}
        </aside>

        {/* Content */}
        <section className="tw-content">
          <div className="tw-header">
            <div className="tw-header-left">
              <h2 className="tw-page-title">Table <span>Manager</span></h2>
              <div className="tw-stat-pills">
                <span className="tw-pill available"><span className="tw-pill-dot"/>{stats.available} Open</span>
                <span className="tw-pill occupied"><span className="tw-pill-dot"/>{stats.occupied} Busy</span>
                {stats.reserved  > 0 && <span className="tw-pill reserved"><span className="tw-pill-dot"/>{stats.reserved} Reserved</span>}
                {stats.attention > 0 && <span className="tw-pill attention"><span className="tw-pill-dot"/>{stats.attention} Attention</span>}
              </div>
            </div>
            <div className="tw-header-right">
              {mergeMode ? (
                <>
                  <button className="tw-btn confirm" onClick={handleConfirmMerge} disabled={saving || mergeSelection.length < 2}>
                    {saving ? <Loader2 size={14} className="tw-spin"/> : <CheckCircle2 size={15}/>} Confirm Merge ({mergeSelection.length})
                  </button>
                  <button className="tw-btn cancel" onClick={cancelMergeMode}><X size={14}/> Cancel</button>
                </>
              ) : (
                <button className="tw-btn" onClick={enterMergeMode}><LinkIcon size={14}/> Merge Tables</button>
              )}
            </div>
          </div>

          {mergeMode && (
            <div className="tw-merge-banner">
              <div className="tw-merge-banner-icon"><MousePointerClick size={20}/></div>
              <div className="tw-merge-banner-body">
                <div className="tw-merge-banner-title">Merge Mode Active</div>
                <div className="tw-merge-banner-steps">
                  <span className="tw-step"><span className="tw-step-num">1</span>Click tables to select<ArrowRight size={11}/></span>
                  <span className="tw-step"><span className="tw-step-num">2</span>Pick at least 2<ArrowRight size={11}/></span>
                  <span className="tw-step"><span className="tw-step-num">3</span>Click <strong>Confirm Merge</strong></span>
                </div>
              </div>
              {mergeSelection.length > 0 && (
                <div className="tw-merge-selected-list">
                  {mergeSelection.map(id => {
                    const t = tables.find(x => x.id === id);
                    return t ? (
                      <span key={id} className="tw-merge-chip">
                        {t.label}<button onClick={() => toggleMergeTable(t)}><X size={10}/></button>
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          )}

          {filteredTables.merged.length > 0 && (
            <div className="tw-section">
              <div className="tw-section-title">
                <Layers size={13}/> Merged Groups
                <span className="tw-section-count">{filteredTables.merged.length}</span>
              </div>
              <div className="tw-grid">
                {filteredTables.merged.map(t => (
                  <GroupCard key={t.groupId} members={t._groupMembers} tableItemsMap={tableItemsMap}
                    isSelected={t._groupMembers.some(m => m.id === selectedId)}
                    isMerging={t._groupMembers.some(m => mergeSelection.includes(m.id))}
                    mergeMode={mergeMode} onClick={() => handleTableClick(t)}
                    onQR={() => { setQrTable(t._groupMembers[0]); setShowQR(true); }}/>
                ))}
              </div>
            </div>
          )}

          {filteredTables.occupied.length > 0 && (
            <div className="tw-section">
              <div className="tw-section-title">
                <Utensils size={13}/> Occupied
                <span className="tw-section-count">{filteredTables.occupied.length}</span>
              </div>
              <div className="tw-grid">
                {filteredTables.occupied.map(t => (
                  <TableCard key={t.id} table={t} tableItems={tableItemsMap[t.id] || []}
                    isSelected={selectedId === t.id} isMerging={mergeSelection.includes(t.id)}
                    mergeMode={mergeMode} onClick={handleTableClick}
                    onQR={table => { setQrTable(table); setShowQR(true); }}/>
                ))}
              </div>
            </div>
          )}

          {filteredTables.available.length > 0 && (
            <div className="tw-section">
              <div className="tw-section-title">
                <CheckCircle2 size={13}/> Available
                <span className="tw-section-count">{filteredTables.available.length}</span>
              </div>
              <div className="tw-grid">
                {filteredTables.available.map(t => (
                  <TableCard key={t.id} table={t} tableItems={tableItemsMap[t.id] || []}
                    isSelected={selectedId === t.id} isMerging={mergeSelection.includes(t.id)}
                    mergeMode={mergeMode} onClick={handleTableClick}
                    onQR={table => { setQrTable(table); setShowQR(true); }}/>
                ))}
              </div>
            </div>
          )}

          {tables.length === 0 && (
            <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:10, color:'var(--light-muted)', textAlign:'center', marginTop:80 }}>
              <UtensilsCrossed size={48} strokeWidth={1}/>
              <p style={{ fontWeight:700, fontSize:15 }}>No tables found</p>
              <p style={{ fontSize:13 }}>Add tables from the Floor Plan designer</p>
            </div>
          )}
        </section>

        {/* RIGHT SIDEBAR */}
        <aside className={`tw-sidebar ${selectedTable && !mergeMode ? 'open' : ''}`}>
          <div className="tw-sb-inner">
            {selectedTable && !mergeMode && groupContext ? (
              <>
                {/* Header */}
                <div className="tw-sb-header">
                  <div className="tw-sb-title-block">
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <div className="tw-sb-title">
                        {groupContext.isGroup ? `Group ${selectedTable.groupId}` : `Table ${selectedTable.label}`}
                      </div>
                      <button
                        onClick={() => { setQrTable(selectedTable); setShowQR(true); }}
                        style={{ display:'flex', alignItems:'center', gap:4, padding:'4px 9px', borderRadius:7, border:'1px solid rgba(255,140,0,0.25)', background:'rgba(255,140,0,0.07)', color:'#ff8c00', fontSize:10, fontWeight:700, cursor:'pointer' }}
                      >
                        <QrCode size={11}/> QR Code
                      </button>
                    </div>
                    <div className="tw-sb-sub">
                      {groupContext.isGroup
                        ? groupContext.members.map(m => m.label).join(' + ') + ` · ${groupContext.totalCapacity} seats`
                        : `${selectedTable.shape ?? 'Table'} · Cap. ${selectedTable.capacity}`}
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginTop:4 }}>
                      <div className={`tw-sb-status-badge ${groupContext.status}`}>
                        <span style={{ width:7, height:7, borderRadius:'50%', display:'inline-block', marginRight:3,
                          background: groupContext.status === 'available' ? 'var(--mint)'
                            : groupContext.status === 'reserved'  ? 'var(--saffron)'
                            : groupContext.status === 'attention' ? 'var(--lavender)'
                            : 'var(--tomato)' }}/>
                        {groupContext.status === 'available'  ? 'Available'
                          : groupContext.status === 'reserved'  ? 'Reserved'
                          : groupContext.status === 'attention' ? 'Needs Attention'
                          : 'Occupied'}
                      </div>
                      {isOccupied && qrItemCount > 0 && (
                        <div style={{ display:'flex', alignItems:'center', gap:3, fontSize:9, fontWeight:600, color:'#6366f1' }}>
                          <Smartphone size={9}/> {qrItemCount} QR item{qrItemCount !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                  <button className="tw-sb-close" onClick={() => setSelectedId(null)}><X size={15}/></button>
                </div>

                {/* Tab bar */}
                {isOccupied && (
                  <div style={{ display:'flex', gap:0, borderBottom:'1px solid var(--border)', flexShrink:0 }}>
                    {[
                      { id:'order',       icon:<Utensils size={11}/>,    label:'Order'   },
                      { id:'bill',        icon:<Receipt size={11}/>,     label:'Bill'    },
                      { id:'open_orders', icon:<ListOrdered size={11}/>, label:'History' },
                    ].map(tab => (
                      <button key={tab.id} onClick={() => setSidebarTab(tab.id)}
                        style={{ flex:1, padding:'8px 4px', border:'none',
                          background: sidebarTab === tab.id ? 'rgba(255,77,46,0.08)' : 'transparent',
                          color: sidebarTab === tab.id ? 'var(--tomato)' : 'var(--muted)',
                          fontSize:10, fontWeight:700, cursor:'pointer',
                          borderBottom: sidebarTab === tab.id ? '2px solid var(--tomato)' : '2px solid transparent',
                          display:'flex', alignItems:'center', justifyContent:'center', gap:4, transition:'all 0.15s' }}>
                        {tab.icon}{tab.label}
                      </button>
                    ))}
                  </div>
                )}

                <div className="tw-sb-body">

                  {/* ── AVAILABLE / RESERVED ── */}
                  {(groupContext.status === 'available' || groupContext.status === 'reserved') && (
                    <div className="tw-checkin">
                      <div className="tw-checkin-icon" style={groupContext.status === 'reserved' ? { color:'#b87200' } : {}}>
                        {groupContext.status === 'reserved' ? <BookMarked size={28}/> : <UserPlus size={28}/>}
                      </div>
                      <h4>{groupContext.status === 'reserved' ? 'Reserved' : 'Open'} {groupContext.isGroup ? 'Group' : 'Table'}</h4>
                      {groupContext.isGroup && (
                        <div className="tw-gcap-bar">
                          {groupContext.members.map((m, i) => (
                            <React.Fragment key={m.id}>
                              <div className="tw-gcap-chip">
                                <span className="tw-gcap-lbl">{m.label}</span>
                                <span className="tw-gcap-num"><Users size={9}/> {m.capacity}</span>
                              </div>
                              {i < groupContext.members.length - 1 && <span className="tw-gcap-plus">+</span>}
                            </React.Fragment>
                          ))}
                          <span className="tw-gcap-eq">= {groupContext.totalCapacity} seats</span>
                        </div>
                      )}
                      <div style={{ width:'100%' }}>
                        <div className="tw-prop-label" style={{ display:'flex', alignItems:'center', gap:6 }}>
                          Total Guests
                          {groupContext.isGroup && (
                            <span style={{ fontSize:10, color:'var(--tomato)', background:'rgba(255,77,46,0.09)', padding:'1px 8px', borderRadius:100, fontWeight:700 }}>
                              max {groupContext.totalCapacity}
                            </span>
                          )}
                        </div>
                        {groupContext.totalCapacity > 8 ? (
                          <div className="tw-guest-stepper">
                            <button className="tw-stepper-btn" onClick={() => setGuestCount(c => Math.max(1, c - 1))}><Minus size={16}/></button>
                            <span className="tw-stepper-val">{guestCount}</span>
                            <button className="tw-stepper-btn" onClick={() => setGuestCount(c => Math.min(groupContext.totalCapacity, c + 1))}><Plus size={16}/></button>
                          </div>
                        ) : (
                          <div className="tw-guest-grid" style={{ gridTemplateColumns:`repeat(${Math.min(groupContext.totalCapacity, 6)}, 1fr)` }}>
                            {Array.from({ length: groupContext.totalCapacity }, (_, i) => i + 1).map(n => (
                              <button key={n} className={`tw-guest-btn ${guestCount === n ? 'active' : ''}`} onClick={() => setGuestCount(n)}>{n}</button>
                            ))}
                          </div>
                        )}
                      </div>
                      <button className="tw-action-main" style={{ width:'100%' }} onClick={handleOpenTable} disabled={saving}>
                        {saving ? <Loader2 size={15} className="tw-spin"/> : <UserPlus size={15}/>}
                        {groupContext.status === 'reserved' ? 'Seat' : 'Start'} Session · {guestCount} guests
                        {groupContext.isGroup && ` · ${groupContext.members.length} tables`}
                      </button>
                      {groupContext.status === 'available' && (
                        <button className="tw-checkout-btn" style={{ marginTop:4 }} onClick={handleReserveTable} disabled={saving}>
                          <BookMarked size={15}/> Reserve {groupContext.isGroup ? 'Group' : 'Table'}
                        </button>
                      )}
                      {groupContext.status === 'reserved' && (
                        <button className="tw-checkout-btn" style={{ borderColor:'rgba(255,77,46,0.25)', color:'var(--tomato)', marginTop:4 }} onClick={handleCancelReservation}>
                          <X size={14}/> Cancel Reservation
                        </button>
                      )}
                    </div>
                  )}

                  {/* ── OCCUPIED — ORDER TAB ── */}
                  {isOccupied && sidebarTab === 'order' && (
                    <>
                      {groupContext.isGroup && (
                        <div>
                          <div className="tw-prop-label">Tables in Group</div>
                          <div className="tw-member-chips">
                            {groupContext.members.map(m => (
                              <button key={m.id} className={`tw-member-chip ${m.id === selectedId ? 'active' : ''}`}
                                onClick={() => setSelectedId(m.id)}>
                                <span className="tw-mc-label">{m.label}</span>
                                <span className="tw-mc-guests"><Users size={8}/> {m.guests||0}</span>
                              </button>
                            ))}
                            <div className="tw-mc-total"><Users size={9}/> {groupContext.totalGuests} total</div>
                          </div>
                        </div>
                      )}

                      <div>
                        <div className="tw-prop-label" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                          <span>
                            {groupContext.isGroup ? `All Orders (${groupContext.members.length} tables)` : 'Orders'}
                            {qrItemCount > 0 && (
                              <span style={{ marginLeft:6, fontSize:9, fontWeight:700, background:'rgba(99,102,241,0.12)', color:'#6366f1', padding:'1px 6px', borderRadius:100 }}>
                                {qrItemCount} from QR
                              </span>
                            )}
                          </span>
                          <div style={{ display:'flex', gap:5, alignItems:'center' }}>
                            <button onClick={() => fetchTableCart(selectedId)} title="Refresh from DB"
                              style={{ border:'none', background:'none', cursor:'pointer', display:'flex', padding:'2px', color:'var(--muted)' }}>
                              <RefreshCw size={11}/>
                            </button>
                            <button onClick={() => { setMenuSearch(''); setMenuCat('all'); setShowOrder(true); }}
                              style={{ fontSize:10, fontWeight:700, color:'var(--tomato)', background:'rgba(255,77,46,0.09)', border:'none', borderRadius:6, padding:'2px 8px', cursor:'pointer', display:'flex', alignItems:'center', gap:3 }}>
                              <Plus size={10}/> Add
                            </button>
                          </div>
                        </div>
                        <div className="tw-order-list">
                          {groupContext.allItems.length > 0 ? (
                            groupContext.allItems.map((item, i) => (
                              <div key={`${item.tableId}_${item.cartId}_${i}`} className="tw-order-item" style={{ alignItems:'center' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:4, flex:1, minWidth:0 }}>
                                  {groupContext.isGroup && <span className="table-tag">{item.tableLabel}</span>}
                                  <span style={{ fontSize:12, fontWeight:600, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                    {item.name}{item.variant === 'half' ? ' ½' : ''}
                                  </span>
                                  {item.fromQR && (
                                    <span style={{ fontSize:8, fontWeight:700, background:'rgba(99,102,241,0.12)', color:'#6366f1', padding:'1px 5px', borderRadius:4, flexShrink:0 }}>QR</span>
                                  )}
                                </div>
                                <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
                                  <button onClick={() => handleUpdateItemQty(item.tableId, item.cartId, -1)} style={{ width:18, height:18, borderRadius:4, border:'1px solid rgba(0,0,0,0.12)', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}><Minus size={9}/></button>
                                  <span style={{ fontSize:11, fontWeight:700, minWidth:14, textAlign:'center' }}>{item.qty}</span>
                                  <button onClick={() => handleUpdateItemQty(item.tableId, item.cartId, +1)} style={{ width:18, height:18, borderRadius:4, border:'1px solid rgba(0,0,0,0.12)', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}><Plus size={9}/></button>
                                  <span style={{ fontSize:11, fontWeight:700, color:'var(--tomato)', minWidth:36, textAlign:'right' }}>₹{(parseFloat(item.price) * item.qty).toFixed(0)}</span>
                                  <button onClick={() => handleRemoveItem(item.tableId, item.cartId)} style={{ width:18, height:18, borderRadius:4, border:'none', background:'rgba(239,68,68,0.09)', color:'#ef4444', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}><X size={9}/></button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="tw-empty-order"><Coffee size={28} strokeWidth={1}/><p>No items yet</p></div>
                          )}
                        </div>
                      </div>

                      {groupContext.allItems.length > 0 && (
                        <div style={{ background:'rgba(0,0,0,0.03)', borderRadius:8, padding:'8px 10px', fontSize:11 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', color:'var(--muted)' }}>
                            <span>{groupContext.allItems.reduce((s,i) => s + i.qty, 0)} items</span>
                            <span style={{ fontWeight:800, color:'var(--tomato)', fontSize:13 }}>₹{groupContext.bill.grandTotal.toFixed(0)}</span>
                          </div>
                        </div>
                      )}

                      <div className="tw-sb-actions">
                        <button className="tw-action-main" onClick={() => { setMenuSearch(''); setMenuCat('all'); setShowOrder(true); }}>
                          <Plus size={15}/>{groupContext.isGroup ? `Add to ${selectedTable.label}` : 'Quick Add Item'}
                        </button>
                        <div className="tw-action-row">
                          {groupContext.isGroup && (
                            <button className="tw-action-sub" onClick={handleUnlink}><Split size={14}/> Split</button>
                          )}
                          <button className="tw-action-sub" onClick={handleMarkAttention}
                            style={groupContext.status === 'attention' ? { background:'rgba(139,92,246,0.09)', color:'var(--lavender)', borderColor:'rgba(139,92,246,0.28)' } : {}}>
                            <AlertCircle size={14}/> Flag
                          </button>
                          <button className="tw-action-sub danger" onClick={handleSettleBill}><Trash2 size={14}/> Void</button>
                        </div>
                        {groupContext.allItems.length > 0 && (
                          <button className="tw-checkout-btn" onClick={() => setSidebarTab('bill')}
                            style={{ background:'var(--tomato)', color:'#fff', border:'none', fontWeight:700 }}>
                            <Receipt size={14}/> Proceed to Billing · ₹{groupContext.bill.grandTotal.toFixed(0)}
                          </button>
                        )}
                      </div>
                    </>
                  )}

                  {/* ── OCCUPIED — BILL TAB ── */}
                  {isOccupied && sidebarTab === 'bill' && (
                    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                      <div>
                        <div className="tw-prop-label">Customer Info</div>
                        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                          {[
                            ['customerName',  'Customer name *', <User size={11}/>,         'text'],
                            ['customerPhone', 'Phone *',         <Phone size={11}/>,        'tel' ],
                            ['orderNote',     'Order note…',     <MessageSquare size={11}/>, 'text'],
                          ].map(([key, ph, ico, type]) => (
                            <div key={key} style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(0,0,0,0.04)', borderRadius:8, padding:'6px 10px' }}>
                              {React.cloneElement(ico, { style:{ color:'var(--muted)', flexShrink:0 } })}
                              <input placeholder={ph} type={type} value={billing[key]}
                                onChange={e => patchActiveBilling({ [key]: e.target.value })}
                                style={{ border:'none', background:'none', outline:'none', fontSize:12, flex:1, color:'var(--ink)' }}/>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="tw-prop-label">Bill Summary</div>
                        <div className="tw-bill">
                          {groupContext.isGroup && groupContext.members.map(m => {
                            const mSub = (tableItemsMap[m.id] || []).reduce((s, i) => s + (parseFloat(i.price) * (i.qty || 1)), 0);
                            if (!mSub) return null;
                            return (
                              <div key={m.id} className="tw-bill-row" style={{ fontSize:11 }}>
                                <span style={{ color:'var(--tomato)', fontWeight:700 }}>{m.label}</span>
                                <span>₹{mSub.toFixed(2)}</span>
                              </div>
                            );
                          })}
                          {groupContext.isGroup && <hr style={{ border:'none', borderTop:'1px solid var(--border)', margin:'2px 0' }}/>}
                          <div className="tw-bill-row"><span>Subtotal</span><span>₹{groupContext.bill.subtotal.toFixed(2)}</span></div>
                        </div>
                      </div>

                      <div>
                        <button onClick={() => setShowDiscount(v => !v)}
                          style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 10px', borderRadius:8, border:'1px solid rgba(0,0,0,0.10)', background:'#fff', cursor:'pointer', fontSize:11, fontWeight:700, color: groupContext.bill.discountAmount > 0 ? '#16a34a' : 'var(--muted)' }}>
                          <span style={{ display:'flex', alignItems:'center', gap:5 }}>
                            <Tag size={10}/>
                            {groupContext.bill.discountAmount > 0 ? `Discount −₹${groupContext.bill.discountAmount.toFixed(2)}` : 'Add Discount'}
                          </span>
                          <ChevronDown size={9} style={{ transform: showDiscount ? 'rotate(180deg)' : 'none', transition:'0.2s' }}/>
                        </button>
                        {showDiscount && (
                          <div style={{ padding:'8px', background:'rgba(0,0,0,0.03)', borderRadius:8, marginTop:4, display:'flex', gap:6, alignItems:'center' }}>
                            <div style={{ display:'flex', borderRadius:6, overflow:'hidden', border:'1px solid rgba(0,0,0,0.10)', flexShrink:0 }}>
                              {[['percent','%'],['flat','₹']].map(([id,lbl]) => (
                                <button key={id} onClick={() => patchActiveBilling({ discountType: id })}
                                  style={{ padding:'4px 8px', border:'none', fontSize:10, fontWeight:700, cursor:'pointer', background: billing.discountType === id ? 'var(--tomato)' : '#fff', color: billing.discountType === id ? '#fff' : 'var(--muted)' }}>{lbl}</button>
                              ))}
                            </div>
                            <input type="number" min="0" placeholder={billing.discountType === 'percent' ? '10' : '50'}
                              value={billing.discountVal} onChange={e => patchActiveBilling({ discountVal: e.target.value })}
                              style={{ flex:1, border:'1px solid rgba(0,0,0,0.10)', borderRadius:6, padding:'4px 8px', fontSize:12, outline:'none' }}/>
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="tw-prop-label">GST</div>
                        <div style={{ display:'flex', gap:4, alignItems:'center', flexWrap:'wrap' }}>
                          {GST_RATES.map(r => (
                            <button key={r} onClick={() => patchActiveBilling({ gstRate: r })}
                              style={{ padding:'3px 7px', borderRadius:5, border:'1px solid', fontSize:9, fontWeight:700, cursor:'pointer',
                                background: billing.gstRate === r ? '#ff8c00' : '#fff',
                                borderColor: billing.gstRate === r ? '#ff8c00' : 'rgba(0,0,0,0.10)',
                                color: billing.gstRate === r ? '#fff' : 'var(--muted)' }}>{r}%</button>
                          ))}
                          <div style={{ marginLeft:'auto', display:'flex', gap:3 }}>
                            {[['exclusive','Excl.'],['inclusive','Incl.']].map(([id,lbl]) => (
                              <button key={id} onClick={() => patchActiveBilling({ gstType: id })}
                                style={{ padding:'3px 7px', borderRadius:5, border:'1px solid', fontSize:9, fontWeight:700, cursor:'pointer',
                                  background: billing.gstType === id ? '#6366f1' : '#fff',
                                  borderColor: billing.gstType === id ? '#6366f1' : 'rgba(0,0,0,0.10)',
                                  color: billing.gstType === id ? '#fff' : 'var(--muted)' }}>{lbl}</button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="tw-bill">
                        {groupContext.bill.discountAmount > 0 && (
                          <div className="tw-bill-row" style={{ color:'#16a34a' }}>
                            <span>Discount</span><span>−₹{groupContext.bill.discountAmount.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="tw-bill-row">
                          <span>GST {groupContext.bill.gstRate}% <span style={{ fontSize:9, opacity:0.6 }}>({groupContext.bill.gstType === 'inclusive' ? 'incl.' : 'excl.'})</span></span>
                          <span>₹{groupContext.bill.taxAmount.toFixed(2)}</span>
                        </div>
                        <div className="tw-bill-row total"><span>Total</span><span>₹{groupContext.bill.grandTotal.toFixed(2)}</span></div>
                      </div>

                      <div>
                        <div className="tw-prop-label">Payment</div>
                        <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                          {PAYMENT_METHODS.map(pm => (
                            <button key={pm.id} onClick={() => patchActiveBilling({ payMethod: pm.id })}
                              style={{ flex:'1 0 auto', padding:'6px 8px', borderRadius:7, border:'1px solid', fontSize:10, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:4,
                                background: billing.payMethod === pm.id ? 'rgba(255,77,46,0.09)' : '#fff',
                                borderColor: billing.payMethod === pm.id ? 'var(--tomato)' : 'rgba(0,0,0,0.10)',
                                color: billing.payMethod === pm.id ? 'var(--tomato)' : 'var(--muted)' }}>
                              {pm.icon}{pm.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {billing.payMethod === 'split' && (
                        <div style={{ background:'rgba(0,0,0,0.03)', borderRadius:8, padding:'8px 10px', display:'flex', flexDirection:'column', gap:6 }}>
                          {[['splitCash','Cash',<Banknote size={10}/>],['splitCard','Card',<CreditCard size={10}/>],['splitUpi','UPI',<Smartphone size={10}/>]].map(([k,lbl,ico]) => (
                            <div key={k} style={{ display:'flex', alignItems:'center', gap:6, fontSize:11 }}>
                              {ico}
                              <span style={{ flex:1, fontWeight:600, color:'var(--muted)' }}>{lbl}</span>
                              <input type="number" min="0" placeholder="0" value={billing[k]}
                                onChange={e => patchActiveBilling({ [k]: e.target.value })}
                                style={{ width:70, border:'1px solid rgba(0,0,0,0.12)', borderRadius:6, padding:'4px 6px', fontSize:12, textAlign:'right', outline:'none' }}/>
                            </div>
                          ))}
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:10, fontWeight:700, paddingTop:4, borderTop:'1px solid rgba(0,0,0,0.08)', color: Math.abs(splitTotal - groupContext.bill.grandTotal) < 0.01 ? '#16a34a' : '#ef4444' }}>
                            {Math.abs(splitTotal - groupContext.bill.grandTotal) < 0.01
                              ? <><CheckCircle2 size={10}/> Balanced</>
                              : <>Remaining: ₹{(groupContext.bill.grandTotal - splitTotal).toFixed(2)}</>}
                          </div>
                        </div>
                      )}

                      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                        <div style={{ display:'flex', gap:6 }}>
                          <button onClick={() => handlePlaceOrder('placed')} disabled={placing || !groupContext.allItems.length}
                            style={{ flex:1, padding:'9px', borderRadius:8, border:'none', background:'var(--tomato)', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5, opacity: placing ? 0.7 : 1 }}>
                            {placing ? <Loader2 size={12} className="tw-spin"/> : <CheckCircle2 size={12}/>} Place Order
                          </button>
                          <button onClick={() => handlePlaceOrder('kot')} disabled={placing || !groupContext.allItems.length}
                            style={{ padding:'9px 12px', borderRadius:8, border:'1px solid rgba(0,0,0,0.12)', background:'#fff', fontSize:11, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:5, color:'var(--ink)' }}>
                            <Printer size={11}/> KOT
                          </button>
                        </div>
                        <button onClick={() => handlePlaceOrder('placed_print')} disabled={placing || !groupContext.allItems.length}
                          style={{ width:'100%', padding:'8px', borderRadius:8, border:'1px solid rgba(0,0,0,0.12)', background:'#fff', fontSize:11, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5, color:'var(--ink)' }}>
                          <Printer size={11}/> Place &amp; Print Bill
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── OPEN ORDERS TAB ── */}
                  {isOccupied && sidebarTab === 'open_orders' && (
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <span style={{ fontSize:11, fontWeight:700, color:'var(--muted)' }}>Recent Orders</span>
                        <button onClick={loadOpenOrders} disabled={ordersLoading}
                          style={{ border:'none', background:'none', cursor:'pointer', display:'flex', color:'var(--muted)', padding:4 }}>
                          {ordersLoading ? <Loader2 size={13} className="tw-spin"/> : <RefreshCw size={13}/>}
                        </button>
                      </div>
                      {ordersLoading && !openOrders.length
                        ? <div style={{ padding:20, textAlign:'center', color:'var(--muted)' }}><Loader2 size={18} className="tw-spin"/></div>
                        : openOrders.length === 0
                          ? (
                            <div style={{ padding:20, textAlign:'center', color:'var(--muted)', fontSize:12 }}>
                              <ListOrdered size={24} style={{ opacity:0.4, marginBottom:6 }}/><p>No open orders</p>
                            </div>
                          )
                          : openOrders.map(order => (
                            <TableOpenOrderCard key={order.id} order={order}
                              completing={completingId === order.id}
                              onComplete={() => handleCompleteOrder(order)}
                              onCancel={() => { setCancelModalOrder(order); setCancelReason(''); }}
                              onViewBill={() => setShowBillModal(order)}
                              onPrint={() => {
                                const items = typeof order.items === 'string' ? JSON.parse(order.items || '[]') : (order.items || []);
                               printTableBill({
  items:         typeof order.items === 'string' ? JSON.parse(order.items || '[]') : (order.items || []),
  tableLabel:    order.table_label,
  orderNumber:   order.order_number,
  customerName:  order.customer_name,
  customerPhone: order.customer_phone,
  orderNote:     order.order_note,
  payMethod:     order.payment_method,
  splitCash:     order.split_cash,
  splitCard:     order.split_card,
  splitUpi:      order.split_upi,
  subtotal:      order.subtotal,
  taxAmount:     order.tax_amount,
  grandTotal:    order.grand_total,
  discountAmount:order.discount_amount,
  gstRate:       order.gst_rate,
  gstType:       order.gst_type,
}, restaurantProfile);
                              }}
                              onWhatsApp={() => {
                                const items = typeof order.items === 'string' ? JSON.parse(order.items || '[]') : (order.items || []);
                                sendBillWhatsApp({ items, tableLabel: order.table_label, customerName: order.customer_name, customerPhone: order.customer_phone, subtotal: order.subtotal, taxAmount: order.tax_amount, grandTotal: order.grand_total, payMethod: order.payment_method }, restaurantName);
                              }}
                            />
                          ))
                      }
                    </div>
                  )}

                </div>
              </>
            ) : (
              <div className="tw-sb-empty">
                <Coffee size={44} strokeWidth={1} style={{ color:'var(--light-muted)' }}/>
                <p>Select a table to view details and manage orders</p>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Quick Add Modal */}
      {showOrder && (
        <div className="tw-modal-overlay" onClick={() => setShowOrder(false)}>
          <div className="tw-modal" onClick={e => e.stopPropagation()} style={{ maxHeight:'80vh', display:'flex', flexDirection:'column' }}>
            <div className="tw-modal-header">
              <div className="tw-modal-title">
                Add to {selectedTable?.label}
                {groupContext?.isGroup && <span style={{ fontSize:12, color:'var(--muted)', marginLeft:6 }}>· Group {selectedTable?.groupId}</span>}
              </div>
              <button className="tw-modal-close" onClick={() => setShowOrder(false)}><X size={14}/></button>
            </div>
            <div style={{ padding:'8px 14px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(0,0,0,0.04)', borderRadius:8, padding:'5px 10px', marginBottom:8 }}>
                <Search size={12} style={{ color:'var(--muted)', flexShrink:0 }}/>
                <input placeholder="Search menu…" value={menuSearch} onChange={e => setMenuSearch(e.target.value)} autoFocus
                  style={{ border:'none', background:'none', outline:'none', fontSize:12, flex:1, color:'var(--ink)' }}/>
                {menuSearch && <button onClick={() => setMenuSearch('')} style={{ border:'none', background:'none', cursor:'pointer', display:'flex', padding:0 }}><X size={10}/></button>}
              </div>
              <div style={{ display:'flex', gap:5, overflowX:'auto', paddingBottom:2 }}>
                <button onClick={() => setMenuCat('all')}
                  style={{ flexShrink:0, padding:'3px 10px', borderRadius:100, border:'1px solid', fontSize:10, fontWeight:700, cursor:'pointer',
                    background: menuCat === 'all' ? 'var(--tomato)' : '#fff',
                    borderColor: menuCat === 'all' ? 'var(--tomato)' : 'rgba(0,0,0,0.10)',
                    color: menuCat === 'all' ? '#fff' : 'var(--muted)' }}>All</button>
                {categories.map(c => (
                  <button key={c.id} onClick={() => setMenuCat(String(c.id))}
                    style={{ flexShrink:0, padding:'3px 10px', borderRadius:100, border:'1px solid', fontSize:10, fontWeight:700, cursor:'pointer',
                      background: menuCat === String(c.id) ? 'var(--tomato)' : '#fff',
                      borderColor: menuCat === String(c.id) ? 'var(--tomato)' : 'rgba(0,0,0,0.10)',
                      color: menuCat === String(c.id) ? '#fff' : 'var(--muted)' }}>
                    {c.icon && <span style={{ marginRight:3 }}>{c.icon}</span>}{c.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="tw-modal-body" style={{ flex:1, overflowY:'auto' }}>
              {menuLoading
                ? <div style={{ padding:24, textAlign:'center', color:'var(--muted)' }}><Loader2 size={20} className="tw-spin"/></div>
                : filteredMenu.length === 0
                  ? <div style={{ padding:24, textAlign:'center', color:'var(--muted)', fontSize:13 }}>No items found</div>
                  : filteredMenu.map(product => (
                    <div key={product.id} className="tw-menu-item">
                      <div style={{ flex:1, minWidth:0 }}>
                        <div className="tw-menu-name">{product.name}</div>
                        <div className="tw-menu-cat">
                          {product.tags?.includes('Popular') && <span style={{ color:'#f59e0b', marginRight:4 }}>🔥</span>}
                          {typeof product.category === 'string'
                            ? product.category
                            : (categories.find(c => String(c.id) === String(product.category_id))?.name || '')}
                        </div>
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                        {product.has_half_plate && product.price_half && (
                          <>
                            <div style={{ textAlign:'right' }}>
                              <div style={{ fontSize:9, color:'var(--muted)', fontWeight:600 }}>Half</div>
                              <div className="tw-menu-price" style={{ fontSize:11 }}>₹{product.price_half.toFixed(0)}</div>
                            </div>
                            <button className="tw-menu-add" onClick={e => { e.stopPropagation(); handleAddItem(product, 'half'); }} style={{ fontSize:9, padding:'4px 8px' }}>½</button>
                          </>
                        )}
                        <div style={{ textAlign:'right' }}>
                          {product.has_half_plate && <div style={{ fontSize:9, color:'var(--muted)', fontWeight:600 }}>Full</div>}
                          <div className="tw-menu-price">₹{product.price_full.toFixed(0)}</div>
                        </div>
                        <button className="tw-menu-add" onClick={e => { e.stopPropagation(); handleAddItem(product, 'full'); }}><Plus size={13}/></button>
                      </div>
                    </div>
                  ))
              }
            </div>
          </div>
        </div>
      )}

      {/* Bill View Modal */}
      {showBillModal && (
        <div className="tw-modal-overlay" onClick={() => setShowBillModal(null)}>
          <div className="tw-modal" onClick={e => e.stopPropagation()} style={{ maxHeight:'85vh', display:'flex', flexDirection:'column' }}>
            <div className="tw-modal-header">
              <div className="tw-modal-title">Bill #{showBillModal.order_number}</div>
              <button className="tw-modal-close" onClick={() => setShowBillModal(null)}><X size={14}/></button>
            </div>
            <div style={{ flex:1, overflowY:'auto', padding:'12px 14px' }}>
              <div style={{ textAlign:'center', marginBottom:10 }}>
                <div style={{ fontSize:16, fontWeight:800 }}>{restaurantName}</div>
                <div style={{ fontSize:10, color:'var(--muted)', marginTop:2 }}>TAX INVOICE</div>
                {showBillModal.table_label && <div style={{ fontSize:11, marginTop:4 }}>Table: <b>{showBillModal.table_label}</b></div>}
                {showBillModal.customer_name && <div style={{ fontSize:11, marginTop:2 }}>{showBillModal.customer_name}{showBillModal.customer_phone && ` · ${showBillModal.customer_phone}`}</div>}
                <div style={{ fontSize:10, color:'var(--muted)', marginTop:2 }}>{new Date(showBillModal.created_at || Date.now()).toLocaleString('en-IN')}</div>
              </div>
              <div style={{ borderTop:'1px dashed #ccc', borderBottom:'1px dashed #ccc', padding:'6px 0', margin:'6px 0' }}>
                {(typeof showBillModal.items === 'string' ? JSON.parse(showBillModal.items || '[]') : (showBillModal.items || [])).map((it, i) => (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:11, fontWeight:600, marginBottom:2 }}>
                    <span>{it.name}{it.variant === 'half' ? ' (½)' : ''} ×{it.qty}</span>
                    <span>₹{(parseFloat(it.price) * parseInt(it.qty)).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, margin:'3px 0' }}><span>Subtotal</span><span>₹{parseFloat(showBillModal.subtotal||0).toFixed(2)}</span></div>
              {parseFloat(showBillModal.discount_amount||0) > 0 && (
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#16a34a', margin:'3px 0' }}>
                  <span>Discount</span><span>−₹{parseFloat(showBillModal.discount_amount).toFixed(2)}</span>
                </div>
              )}
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, margin:'3px 0' }}><span>GST ({showBillModal.gst_rate||5}%)</span><span>₹{parseFloat(showBillModal.tax_amount||0).toFixed(2)}</span></div>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:15, fontWeight:900, borderTop:'2px solid #111', paddingTop:5, margin:'5px 0' }}><span>TOTAL</span><span>₹{parseFloat(showBillModal.grand_total||0).toFixed(2)}</span></div>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'var(--muted)' }}><span>Payment</span><span>{(showBillModal.payment_method||'cash').toUpperCase()}</span></div>
              {showBillModal.order_note && <div style={{ fontSize:10, color:'var(--muted)', marginTop:4, fontStyle:'italic' }}>Note: {showBillModal.order_note}</div>}
              <div style={{ textAlign:'center', fontSize:10, color:'var(--muted)', marginTop:8 }}>Thank you! · Powered by Restrowave</div>
            </div>
            <div style={{ display:'flex', gap:8, padding:'10px 14px', borderTop:'1px solid var(--border)' }}>
              <button onClick={() => {
                const items = typeof showBillModal.items === 'string' ? JSON.parse(showBillModal.items||'[]') : (showBillModal.items||[]);
                printTableBill({
  items:         typeof showBillModal.items === 'string' ? JSON.parse(showBillModal.items||'[]') : (showBillModal.items||[]),
  tableLabel:    showBillModal.table_label,
  orderNumber:   showBillModal.order_number,
  customerName:  showBillModal.customer_name,
  customerPhone: showBillModal.customer_phone,
  orderNote:     showBillModal.order_note,
  payMethod:     showBillModal.payment_method,
  splitCash:     showBillModal.split_cash,
  splitCard:     showBillModal.split_card,
  splitUpi:      showBillModal.split_upi,
  subtotal:      showBillModal.subtotal,
  taxAmount:     showBillModal.tax_amount,
  grandTotal:    showBillModal.grand_total,
  discountAmount:showBillModal.discount_amount,
  gstRate:       showBillModal.gst_rate,
  gstType:       showBillModal.gst_type,
}, restaurantProfile);

              }} style={{ flex:1, padding:'9px', borderRadius:8, border:'1px solid rgba(0,0,0,0.10)', background:'#fff', cursor:'pointer', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                <Printer size={12}/> Print
              </button>
              <button onClick={() => {
                const items = typeof showBillModal.items === 'string' ? JSON.parse(showBillModal.items||'[]') : (showBillModal.items||[]);
                sendBillWhatsApp({ items, tableLabel: showBillModal.table_label, customerName: showBillModal.customer_name, customerPhone: showBillModal.customer_phone, subtotal: showBillModal.subtotal, taxAmount: showBillModal.tax_amount, grandTotal: showBillModal.grand_total, payMethod: showBillModal.payment_method }, restaurantName);
              }} style={{ flex:1, padding:'9px', borderRadius:8, border:'none', background:'#25D366', color:'#fff', cursor:'pointer', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                <Send size={12}/> WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Order Modal */}
      {cancelModalOrder && (
        <div className="tw-modal-overlay" onClick={() => setCancelModalOrder(null)}>
          <div className="tw-modal" onClick={e => e.stopPropagation()} style={{ maxWidth:340 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
              <h3 style={{ fontSize:14, fontWeight:800, color:'var(--ink)', display:'flex', alignItems:'center', gap:6 }}>
                <Ban size={15} style={{ color:'#ef4444' }}/> Cancel Order #{cancelModalOrder.order_number}
              </h3>
              <button onClick={() => setCancelModalOrder(null)} style={{ border:'none', background:'none', cursor:'pointer', display:'flex' }}><X size={14}/></button>
            </div>
            <label style={{ fontSize:11, fontWeight:700, color:'var(--muted)', display:'block', marginBottom:6 }}>Cancellation Reason *</label>
            <textarea placeholder="e.g. Customer changed mind…" value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={3} autoFocus
              style={{ width:'100%', border:'1px solid rgba(0,0,0,0.12)', borderRadius:8, padding:'8px 10px', fontSize:12, outline:'none', resize:'vertical', boxSizing:'border-box' }}/>
            <div style={{ display:'flex', gap:8, marginTop:12 }}>
              <button onClick={() => setCancelModalOrder(null)} style={{ flex:1, padding:'9px', borderRadius:8, border:'1px solid rgba(0,0,0,0.10)', background:'#fff', cursor:'pointer', fontSize:12, fontWeight:700, color:'var(--muted)' }}>Keep Order</button>
              <button onClick={handleCancelOrder} style={{ flex:1, padding:'9px', borderRadius:8, border:'none', background:'#ef4444', color:'#fff', cursor:'pointer', fontSize:12, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
                <Ban size={12}/> Cancel Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Panel */}
      {showQR && qrTable && (
        <QRCodePanel table={qrTable} onClose={() => { setShowQR(false); setQrTable(null); }}/>
      )}

      <div className={`tw-toast ${toast.show ? 'show' : ''}`}>
        <CheckCircle2 size={14} className="tw-toast-icon"/>{toast.msg}
      </div>


      {/* ── New Order / Table Alert Popup ── */}
      {newOrderAlert && (
        <div
          onClick={() => setNewOrderAlert(null)}
          style={{
            position: 'fixed', top: 16, right: 16, zIndex: 9999,
            background: newOrderAlert.type === 'new_order'    ? '#ff4d2e'
                      : newOrderAlert.type === 'item_added'   ? '#f59e0b'
                      : '#6366f1',
            color: '#fff',
            borderRadius: 14,
            padding: '14px 18px',
            minWidth: 260, maxWidth: 340,
            boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
            cursor: 'pointer',
            animation: 'slideInAlert 0.3s ease',
            display: 'flex', alignItems: 'flex-start', gap: 12,
          }}
        >
          <span style={{ fontSize: 26, lineHeight: 1, flexShrink: 0 }}>{newOrderAlert.icon}</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 3 }}>
              {newOrderAlert.type === 'new_order'      ? 'New Order!'
               : newOrderAlert.type === 'item_added'   ? 'Items Added'
               : 'Table Occupied'}
            </div>
            <div style={{ fontSize: 12, opacity: 0.92, lineHeight: 1.4 }}>
              {newOrderAlert.message}
            </div>
            <div style={{ fontSize: 10, opacity: 0.7, marginTop: 5 }}>
              Tap to dismiss
            </div>
          </div>
          <button
            onClick={e => { e.stopPropagation(); setNewOrderAlert(null); }}
            style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, padding: '2px 6px', color: '#fff', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Slide-in animation ── */}
      <style>{`
        @keyframes slideInAlert {
          from { transform: translateX(120%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>


    </div>
  );
}

/* ═══════════════════════════════════════
   TABLE OPEN ORDER CARD
═══════════════════════════════════════ */
function TableOpenOrderCard({ order, completing, onComplete, onCancel, onViewBill, onPrint, onWhatsApp }) {
  const sm      = ORDER_STATUS_META[order.status] || ORDER_STATUS_META.placed;
  const items   = useMemo(() => {
    try { return typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []); }
    catch { return []; }
  }, [order.items]);
  const elapsed = Math.floor((Date.now() - new Date(order.created_at)) / 60000);
  const isQR    = order.order_source === 'qr';
  return (
    <div style={{ background:'rgba(0,0,0,0.03)', borderRadius:10, padding:'9px 10px', display:'flex', flexDirection:'column', gap:6, border: isQR ? '1px solid rgba(99,102,241,0.20)' : 'none' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:5, flexWrap:'wrap' }}>
          <span style={{ fontSize:11, fontWeight:800, color:'var(--ink)' }}>#{order.order_number}</span>
          <span style={{ fontSize:9, fontWeight:700, background:sm.bg, color:sm.color, padding:'2px 7px', borderRadius:100 }}>{sm.label}</span>
          {isQR && <span style={{ fontSize:9, fontWeight:700, background:'rgba(99,102,241,0.12)', color:'#6366f1', padding:'2px 6px', borderRadius:100 }}>📱 QR</span>}
          {order.customer_name && <span style={{ fontSize:9, color:'var(--muted)' }}>{order.customer_name}</span>}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
          <span style={{ fontSize:9, color: elapsed > 20 ? '#ef4444' : 'var(--muted)', fontWeight:600, display:'flex', alignItems:'center', gap:2 }}><Timer size={9}/> {elapsed}m</span>
          <span style={{ fontSize:11, fontWeight:800, color:'var(--tomato)' }}>₹{parseFloat(order.grand_total||0).toFixed(0)}</span>
        </div>
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
        {items.slice(0, 4).map((it, i) => (
          <span key={i} style={{ fontSize:9, background:'rgba(0,0,0,0.06)', borderRadius:4, padding:'1px 5px', color:'var(--ink)' }}>
            {it.name}{it.variant === 'half' ? ' ½' : ''} ×{it.qty}
          </span>
        ))}
        {items.length > 4 && <span style={{ fontSize:9, color:'var(--muted)' }}>+{items.length - 4} more</span>}
      </div>
      <div style={{ display:'flex', gap:4 }}>
        <button onClick={onComplete} disabled={completing} style={{ flex:1, padding:'5px', borderRadius:6, border:'none', background:'rgba(34,197,94,0.12)', color:'#16a34a', fontSize:10, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:3 }}>
          {completing ? <Loader2 size={10} className="tw-spin"/> : <CheckCheck size={10}/>} Complete
        </button>
        <button onClick={onCancel} style={{ flex:1, padding:'5px', borderRadius:6, border:'none', background:'rgba(239,68,68,0.10)', color:'#ef4444', fontSize:10, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:3 }}>
          <Ban size={10}/> Cancel
        </button>
      </div>
      <div style={{ display:'flex', gap:4 }}>
        <button onClick={onViewBill} style={{ flex:1, padding:'4px', borderRadius:6, border:'1px solid rgba(0,0,0,0.10)', background:'#fff', fontSize:9, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:3, color:'var(--ink)' }}><FileText size={9}/> View Bill</button>
        <button onClick={onPrint}    style={{ flex:1, padding:'4px', borderRadius:6, border:'1px solid rgba(0,0,0,0.10)', background:'#fff', fontSize:9, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:3, color:'var(--ink)' }}><Printer size={9}/> Print</button>
        <button onClick={onWhatsApp} style={{ flex:1, padding:'4px', borderRadius:6, border:'none', background:'#25D366', fontSize:9, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:3, color:'#fff' }}><Send size={9}/> WA</button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   GROUP CARD
═══════════════════════════════════════ */
function GroupCard({ members, tableItemsMap, isSelected, isMerging, mergeMode, onClick, onQR }) {
  const totalCap    = members.reduce((s, t) => s + (t.capacity || 0), 0);
  const totalGuests = members.reduce((s, t) => s + (t.guests   || 0), 0);
  const totalAmt    = members.reduce((s, t) =>
    (tableItemsMap[t.id] || []).reduce((is, i) => is + (parseFloat(i.price) * (i.qty || 1)), s), 0);
  const status = members.some(t => t.status === 'attention') ? 'attention'
    : members.some(t => t.status === 'occupied')  ? 'occupied'
    : members.every(t => t.status === 'reserved') ? 'reserved'
    : 'available';
  const groupId = members[0]?.groupId;
  return (
    <div
      className={['tw-table-card', status, 'is-linked', isSelected ? 'is-selected' : '', isMerging ? 'is-merging' : '', mergeMode && !isMerging ? 'merge-eligible' : ''].filter(Boolean).join(' ')}
      style={{ minHeight:150, position:'relative' }} onClick={onClick}
    >
      {mergeMode && (
        <div className={`tw-merge-check ${isMerging ? 'checked' : ''}`}>
          {isMerging ? <CheckCircle2 size={16}/> : <div className="tw-merge-circle"/>}
        </div>
      )}
      {!mergeMode && (
        <button onClick={e => { e.stopPropagation(); onQR && onQR(); }}
          style={{ position:'absolute', top:6, right:6, width:22, height:22, borderRadius:5, border:'none', background:'rgba(255,255,255,0.75)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#8c7355', zIndex:1 }}
          title="QR Code"><QrCode size={11}/></button>
      )}
      <span className="tw-group-badge">{groupId}</span>
      <div className="tw-card-top">
        <div style={{ display:'flex', gap:3, flexWrap:'wrap' }}>
          {members.map(m => (
            <span key={m.id} style={{ fontSize:11, fontWeight:800, background:'rgba(255,77,46,0.09)', color:'var(--tomato)', borderRadius:5, padding:'1px 6px' }}>{m.label}</span>
          ))}
        </div>
        <span className="tw-card-cap"><Users size={10}/> {totalCap}</span>
      </div>
      <div className="tw-card-mid">
        {status === 'available' ? <span className="tw-card-status-label">Ready</span>
          : status === 'reserved' ? <span className="tw-card-status-label">Reserved</span>
          : <><span className="tw-card-amount">₹{totalAmt.toFixed(0)}</span><span className="tw-card-timer"><Clock size={9}/> Group bill</span></>}
      </div>
      <div className="tw-seat-dots">
        {[...Array(Math.min(totalCap, 12))].map((_, i) => (
          <span key={i} className={`tw-seat-dot ${i < totalGuests ? 'filled' : ''}`}/>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   TABLE CARD
═══════════════════════════════════════ */
function TableCard({ table, tableItems, isSelected, isMerging, mergeMode, onClick, onQR }) {
  const total   = tableItems.reduce((s, i) => s + (parseFloat(i.price) * (i.qty || 1)), 0);
  const isLong  = table.timer && table.timer.includes('h');
  const qrCount = tableItems.filter(i => i.fromQR).length;
  return (
    <div
      className={['tw-table-card', table.status, isSelected ? 'is-selected' : '', isMerging ? 'is-merging' : '', mergeMode && !isMerging ? 'merge-eligible' : ''].filter(Boolean).join(' ')}
      style={{ position:'relative' }} onClick={() => onClick(table)}
    >
      {mergeMode && (
        <div className={`tw-merge-check ${isMerging ? 'checked' : ''}`}>
          {isMerging ? <CheckCircle2 size={16}/> : <div className="tw-merge-circle"/>}
        </div>
      )}
      {!mergeMode && (
        <button onClick={e => { e.stopPropagation(); onQR && onQR(table); }}
          style={{ position:'absolute', top:6, right:6, width:22, height:22, borderRadius:5, border:'none', background:'rgba(255,255,255,0.75)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#8c7355', zIndex:1 }}
          title="QR Code"><QrCode size={11}/></button>
      )}
      {qrCount > 0 && (
        <span style={{ position:'absolute', bottom:6, right:6, fontSize:8, fontWeight:800, background:'rgba(99,102,241,0.85)', color:'#fff', padding:'1px 5px', borderRadius:100, zIndex:1 }}>📱{qrCount}</span>
      )}
      <div className="tw-card-top">
        <span className="tw-card-id">{table.label}</span>
        <span className="tw-card-cap"><Users size={10}/> {table.capacity}</span>
      </div>
      <div className="tw-card-mid">
        {table.status === 'available' ? <span className="tw-card-status-label">Ready</span>
          : table.status === 'reserved' ? <span className="tw-card-status-label">Reserved</span>
          : <><span className="tw-card-amount">₹{total.toFixed(0)}</span>{table.timer && <span className={`tw-card-timer ${isLong ? 'warn' : ''}`}><Clock size={9}/> {table.timer}</span>}</>}
      </div>
      <div className="tw-seat-dots">
        {[...Array(Math.min(table.capacity, 8))].map((_, i) => (
          <span key={i} className={`tw-seat-dot ${i < (table.guests||0) ? 'filled' : ''}`}/>
        ))}
      </div>
      {table.shape && <span className="tw-shape-tag">{table.shape}</span>}
    </div>
  );
}

export default Tables;